import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { metaDashboardApi, type VooglamMetaDataset, type VooglamMetaReportParams } from '../api/metaDashboard';
import type { BingDateGrain } from '../api/bingDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { MultiSelect } from '../components/MultiSelect';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { SortableHeader, compareSortValues, type SortState } from '../components/SortableTable';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';
import { fmtInt, fmtNum, fmtPct } from './AppLikeDeliveryDashboard';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';
type MetricKey =
  | 'spend' | 'budget_share' | 'cpm' | 'ctr' | 'cpc' | 'cost_per_add_to_cart'
  | 'cpa' | 'aov' | 'account_roi' | 'click_registration_rate' | 'click_add_to_cart_rate'
  | 'purchase_conversion_rate' | 'click_purchase_rate' | 'impressions' | 'clicks'
  | 'add_to_cart' | 'purchase' | 'purchase_value' | 'complete_registration';

interface DateRange { start: string; end: string; }
interface AppliedTopState extends DateRange { dateGrain: BingDateGrain; }

interface ExportQuerySnapshot {
  params: VooglamMetaReportParams;
  dimensions: string[];
}
interface Dimension { key: string; label: string; }
interface Metric { key: MetricKey; label: string; fmt: (value: number) => string; permKey?: FieldKey; }
interface ReportRow { dimensions: Record<string, string>; metrics: Record<string, number>; }
interface TreeMetricAccumulator {
  budget_base: number;
  budget_share: number;
  spend: number;
  purchase_value: number;
  impressions: number;
  clicks: number;
  add_to_cart: number;
  purchase: number;
  complete_registration: number;
}
interface TreeNode {
  id: string;
  depth: number;
  dimKey: string;
  value: string;
  metrics: TreeMetricAccumulator;
  children: TreeNode[];
  childMap: Map<string, TreeNode>;
}

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const MAIN_DIMENSIONS: Dimension[] = [
  { key: 'date', label: '日期维度' },
  { key: 'traffic_type', label: '账户流量类型' },
  { key: 'user_segment_key', label: '受众细分' },
  { key: 'account_name', label: '广告账户' },
  { key: 'campaign_name_type', label: '广告系列类型' },
  { key: 'creativity_type', label: '创意类型' },
  { key: 'is_new_type', label: '年度新老品类型' },
  { key: 'uptime', label: '年度新品上新时间' },
  { key: 'marketing_event', label: '营销事件' },
  { key: 'image_type', label: '内容细分' },
  { key: 'optimization_goal_val_cn', label: '竞价策略_总' },
  { key: 'bid_strategy_val_cn', label: '竞价策略_细分' },
  { key: 'attribution_model', label: '归因模型' },
];

const COUNTRY_DIMENSIONS: Dimension[] = [
  { key: 'date', label: '日期维度' },
  { key: 'country', label: '地区' },
  { key: 'cn_country', label: '地区简称' },
  { key: 'priority', label: '地区优先级' },
  ...MAIN_DIMENSIONS.filter(item => item.key !== 'user_segment_key'),
];

const MAIN_FILTERS = MAIN_DIMENSIONS.filter(item => item.key !== 'date');
const COUNTRY_FILTERS = COUNTRY_DIMENSIONS.filter(item => item.key !== 'date');

const METRICS: Metric[] = [
  { key: 'spend', label: '账户花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'budget_share', label: '花费占比', fmt: fmtPct, permKey: 'spend' },
  { key: 'cpm', label: '账户CPM', fmt: fmtNum, permKey: 'cpc' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'aov', label: '账户单均价', fmt: fmtNum, permKey: 'revenue' },
  { key: 'account_roi', label: '账户ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'click_registration_rate', label: '点击注册率', fmt: fmtPct },
  { key: 'click_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'purchase_conversion_rate', label: '购物车转化率', fmt: fmtPct },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'add_to_cart', label: '加购次数', fmt: fmtInt },
  { key: 'purchase', label: '购物次数', fmt: fmtInt },
  { key: 'purchase_value', label: '购物转化价值', fmt: fmtNum, permKey: 'revenue' },
  { key: 'complete_registration', label: '完成注册次数', fmt: fmtInt },
];

function daysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function rangeForPreset(preset: DatePreset, customStart: string, customEnd: string): DateRange {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function toFilters(values: Record<string, string[]>): Record<string, string> {
  return Object.fromEntries(Object.entries(values)
    .filter(([, selected]) => selected.length > 0)
    .map(([key, selected]) => [key, selected.join(',')]));
}

function dimensionLabel(key: string, dimensions: Dimension[]): string {
  return dimensions.find(item => item.key === key)?.label ?? key;
}

function placeDateLast(dimensions: string[]): string[] {
  return dimensions.includes('date') ? [...dimensions.filter(key => key !== 'date'), 'date'] : dimensions;
}

function placeCountryFirstAndDateLast(dimensions: string[]): string[] {
  if (!dimensions.includes('country') || !dimensions.includes('date')) return dimensions;
  return ['country', ...dimensions.filter(key => key !== 'country' && key !== 'date'), 'date'];
}

function metricAccumulator(): TreeMetricAccumulator {
  return {
    budget_base: 0,
    budget_share: 0,
    spend: 0,
    purchase_value: 0,
    impressions: 0,
    clicks: 0,
    add_to_cart: 0,
    purchase: 0,
    complete_registration: 0,
  };
}

function addMetrics(target: TreeMetricAccumulator, source: Record<string, number> = {}) {
  target.budget_base += source.budget_base ?? 0;
  target.budget_share += source.budget_share ?? 0;
  target.spend += source.spend ?? 0;
  target.purchase_value += source.purchase_value ?? 0;
  target.impressions += source.impressions ?? 0;
  target.clicks += source.clicks ?? 0;
  target.add_to_cart += source.add_to_cart ?? 0;
  target.purchase += source.purchase ?? 0;
  target.complete_registration += source.complete_registration ?? 0;
}

function metricFromAccumulator(acc: TreeMetricAccumulator, key: MetricKey, totalSpend = acc.budget_base): number {
  switch (key) {
    case 'budget_share': return totalSpend ? acc.spend / totalSpend : acc.budget_share;
    case 'cpm': return acc.impressions ? acc.spend / acc.impressions * 1000 : 0;
    case 'ctr': return acc.impressions ? acc.clicks / acc.impressions : 0;
    case 'cpc': return acc.clicks ? acc.spend / acc.clicks : 0;
    case 'cost_per_add_to_cart': return acc.add_to_cart ? acc.spend / acc.add_to_cart : 0;
    case 'cpa': return acc.purchase ? acc.spend / acc.purchase : 0;
    case 'aov': return acc.purchase ? acc.purchase_value / acc.purchase : 0;
    case 'account_roi': return acc.spend ? acc.purchase_value / acc.spend : 0;
    case 'click_registration_rate': return acc.clicks ? acc.complete_registration / acc.clicks : 0;
    case 'click_add_to_cart_rate': return acc.clicks ? acc.add_to_cart / acc.clicks : 0;
    case 'purchase_conversion_rate': return acc.add_to_cart ? acc.purchase / acc.add_to_cart : 0;
    case 'click_purchase_rate': return acc.clicks ? acc.purchase / acc.clicks : 0;
    default: return acc[key as keyof TreeMetricAccumulator] ?? 0;
  }
}

function rowToAccumulator(row: ReportRow | null): TreeMetricAccumulator {
  const acc = metricAccumulator();
  if (row) addMetrics(acc, row.metrics);
  return acc;
}

function buildTree(rows: ReportRow[], dimensions: string[]): TreeNode[] {
  const root: TreeNode = { id: 'root', depth: -1, dimKey: '', value: '', metrics: metricAccumulator(), children: [], childMap: new Map() };
  rows.forEach(row => {
    addMetrics(root.metrics, row.metrics);
    let parent = root;
    dimensions.forEach((dimension, depth) => {
      const value = row.dimensions[dimension] || '-';
      const id = `${parent.id}/${dimension}:${value}`;
      let node = parent.childMap.get(id);
      if (!node) {
        node = { id, depth, dimKey: dimension, value, metrics: metricAccumulator(), children: [], childMap: new Map() };
        parent.childMap.set(id, node);
        parent.children.push(node);
      }
      addMetrics(node.metrics, row.metrics);
      parent = node;
    });
  });
  return root.children;
}

function visibleTreeRows(nodes: TreeNode[], expanded: Set<string>): TreeNode[] {
  const result: TreeNode[] = [];
  const append = (items: TreeNode[]) => items.forEach(node => {
    result.push(node);
    if (node.children.length > 0 && expanded.has(node.id)) append(node.children);
  });
  append(nodes);
  return result;
}

function expandableTreeNodeIDs(nodes: TreeNode[]): string[] {
  return nodes.flatMap(node => [
    ...(node.children.length > 0 ? [node.id] : []),
    ...expandableTreeNodeIDs(node.children),
  ]);
}

function sortTreeSiblings(nodes: TreeNode[], sort: SortState, userLevel: number): TreeNode[] {
  if (!sort) return nodes;
  const metric = METRICS.find(item => item.key === sort.key);
  if (!metric || (metric.permKey && userLevel < 2)) return nodes;
  return nodes
    .map((node, index) => ({ node, index }))
    .sort((left, right) => {
      const result = compareSortValues(
        metricFromAccumulator(left.node.metrics, metric.key),
        metricFromAccumulator(right.node.metrics, metric.key),
        sort.direction,
      );
      return result === 0 ? left.index - right.index : result;
    })
    .map(({ node }) => ({ ...node, children: sortTreeSiblings(node.children, sort, userLevel) }));
}

function FilterControls({ definitions, values, onChange }: {
  definitions: Dimension[];
  values: Record<string, string[]>;
  onChange: (key: string, next: string[]) => void;
}) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
    {definitions.map(filter => <div key={filter.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 190 }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{filter.label}</label>
      <ReportLabelSelect
        dataset="vooglam_meta_delivery"
        field={filter.key}
        pageFields={definitions.map(item => item.key)}
        value={values[filter.key] ?? []}
        onChange={next => onChange(filter.key, next)}
        width={190}
      />
    </div>)}
  </div>;
}

interface SectionProps {
  title: string;
  dataset: VooglamMetaDataset;
  defaultDimensions: string[];
  dimensionOrder?: 'country_first_date_last' | 'date_last';
  dimensions: Dimension[];
  filters: Dimension[];
  storageKey: string;
  userLevel: number;
  brandID: string;
  siteID: string;
  siteReady: boolean;
  baseParams: AppliedTopState;
  queryVersion: number;
}

function MetaReportSection({ title, dataset, defaultDimensions, dimensionOrder, dimensions, filters, storageKey, userLevel, brandID, siteID, siteReady, baseParams, queryVersion }: SectionProps) {
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [selectedDimensions, setSelectedDimensions] = useState(defaultDimensions);
  const [appliedDimensions, setAppliedDimensions] = useState(defaultDimensions);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [appliedFilterValues, setAppliedFilterValues] = useState<Record<string, string[]>>({});
  const [appliedQueryVersion, setAppliedQueryVersion] = useState(queryVersion);
  const [exportQuery, setExportQuery] = useState<ExportQuerySnapshot | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [summary, setSummary] = useState<ReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>(null);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  useMarketingViewSection('detail:' + storageKey, {
    label: title, state: { selectedDimensions, filterValues, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setSelectedDimensions(value.selectedDimensions); setFilterValues(value.filterValues); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading,
  });

  const visibleMetrics = useMemo(() => METRICS.filter(metric => !hiddenMetricKeys.has(metric.key)), [hiddenMetricKeys]);
  const treeRoots = useMemo(() => buildTree(rows, appliedDimensions), [appliedDimensions, rows]);
  const sortedTreeRoots = useMemo(() => sortTreeSiblings(treeRoots, sort, userLevel), [sort, treeRoots, userLevel]);
  const visibleNodes = useMemo(() => visibleTreeRows(sortedTreeRoots, expanded), [expanded, sortedTreeRoots]);
  const expandableNodeIDs = useMemo(() => expandableTreeNodeIDs(treeRoots), [treeRoots]);
  const queryParams = useMemo((): VooglamMetaReportParams | null => {
    if (!brandID || !siteID) return null;
    return {
      brand_id: brandID, site_id: siteID, start_date: baseParams.start, end_date: baseParams.end, date_grain: baseParams.dateGrain, dataset,
      dimensions: appliedDimensions.join(','), ...toFilters(appliedFilterValues),
    };
  }, [appliedDimensions, appliedFilterValues, baseParams, brandID, dataset, siteID]);

  const fetchReport = useCallback(async () => {
    if (!queryParams) return;
    setLoading(true);
    setError('');
    try {
      const response = await metaDashboardApi.getVooglamReport(queryParams);
      setRows(response.data?.list ?? []);
      setSummary(response.data?.summary ?? null);
      setExportQuery({ params: { ...queryParams }, dimensions: [...appliedDimensions] });
      setExpanded(new Set());
    } catch (reason) {
      setRows([]);
      setSummary(null);
      setExportQuery(null);
      setError(reason instanceof Error ? reason.message : `加载${title}失败`);
    } finally {
      setLoading(false);
    }
  }, [appliedDimensions, queryParams, title]);

  useEffect(() => {
    if (appliedQueryVersion === queryVersion) return;
    setAppliedDimensions(selectedDimensions);
    setAppliedFilterValues(filterValues);
    setAppliedQueryVersion(queryVersion);
  }, [appliedQueryVersion, filterValues, queryVersion, selectedDimensions]);

  useEffect(() => {
    if (appliedQueryVersion !== queryVersion) return;
    if (queryParams) fetchReport();
    else if (siteReady) {
      setRows([]);
      setSummary(null);
      setExportQuery(null);
      setError('');
    }
  }, [appliedQueryVersion, fetchReport, queryParams, queryVersion, siteReady]);
  const changeDimensions = useCallback((next: string[]) => {
    const allowed = new Set(dimensions.map(item => item.key));
    const selected = [...new Set(next.filter(key => allowed.has(key)))];
    const ordered = dimensionOrder === 'country_first_date_last'
      ? placeCountryFirstAndDateLast(selected)
      : dimensionOrder === 'date_last' ? placeDateLast(selected) : selected;
    setSelectedDimensions(ordered.length ? ordered : defaultDimensions);
  }, [defaultDimensions, dimensionOrder, dimensions]);
  const changeFilter = useCallback((key: string, next: string[]) => setFilterValues(current => ({ ...current, [key]: next })), []);
  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const toggleNode = useCallback((id: string) => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setExpanded(new Set(expandableNodeIDs)), [expandableNodeIDs]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);
  const toggleMetric = useCallback((key: string) => {
    setSort(current => current?.key === key ? null : current);
    setHiddenMetricKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, [storageKey]);
  const resetMetrics = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(storageKey); } catch {}
  }, [storageKey]);
  const renderMetric = useCallback((metrics: TreeMetricAccumulator, metric: Metric) => {
    const value = metric.fmt(metricFromAccumulator(metrics, metric.key));
    return metric.permKey ? sf(value, metric.permKey, userLevel).value ?? '锁定' : value;
  }, [userLevel]);
  const summaryMetrics = useMemo(() => rowToAccumulator(summary), [summary]);
  const handleExport = useCallback(async () => {
    if (!exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_meta_v_report',
        title: `Vooglam Meta ${title}`,
        file_name: `Vooglam_Meta_${title}_${exportQuery.params.start_date}_${exportQuery.params.end_date}.csv`,
        brand_id: exportQuery.params.brand_id,
        site_id: exportQuery.params.site_id,
        params: exportQuery.params as unknown as Record<string, unknown>,
        columns: [
          ...exportQuery.dimensions.map(key => ({ key, label: dimensionLabel(key, dimensions) })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : '导出任务提交失败', 'error');
    }
  }, [dimensions, enqueueReportExport, exportQuery, showToast, title, visibleMetrics]);

  return <section style={{
    marginTop: 16,
    background: 'var(--surface)',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(15,23,42,.04)',
  }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div><div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{title}</div><div style={{ marginTop: 2, fontSize: 12, color: 'var(--text3)' }}>筛选条件将在顶部查询后生效</div></div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={expandAll} disabled={loading || expandableNodeIDs.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Maximize2 size={14} strokeWidth={2} />全部展开</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={collapseAll} disabled={loading || expanded.size === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Minimize2 size={14} strokeWidth={2} />全部收起</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={handleExport} disabled={!exportQuery || loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Download size={14} strokeWidth={2} />导出</button>
        <ColumnToggle cols={METRICS.map(metric => ({ key: metric.key, label: metric.label }))} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetric} onReset={resetMetrics} />
      </div>
    </div>
    <div className="data-filter-card" style={{ borderRadius: 0, border: 0, borderBottom: '1px solid var(--border2)', boxShadow: 'none' }}>
      <FilterControls definitions={filters} values={filterValues} onChange={changeFilter} />
      <div className="data-filter-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div className="dimension-select-row" style={{ display: 'flex', alignItems: 'center' }}>
          <span className="dimension-select-label">动态维度</span>
          <MultiSelect options={dimensions.map(item => ({ value: item.key, label: item.label }))} value={selectedDimensions} onChange={changeDimensions} countLabelFormatter={count => `已选 ${count} 项`} tagDisplay style={{ width: 420 }} />
        </div>
      </div>
    </div>
    {error && <div style={{ margin: '12px 16px 0', color: 'var(--red)', fontSize: 13 }}>{error}</div>}
    <div className="table-wrap" style={{ position: 'relative', maxHeight: 620, overflow: 'auto' }}>
      <table style={{ minWidth: Math.max(1280, 160 + visibleMetrics.length * 95) }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}><tr>
          <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: '1%', minWidth: 160, maxWidth: 360, boxShadow: '2px 0 4px rgba(15,23,42,.06)' }}><DimensionHeaderTooltip labels={dimensions.map(dimension => dimension.label)} /></th>
          {visibleMetrics.map(metric => <SortableHeader key={metric.key} sortKey={metric.key} sort={sort} onSort={toggleSort} align="right" disabled={Boolean(metric.permKey && userLevel < 2)} style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}><MetricDescriptionTooltip label={metric.label} /></SortableHeader>)}
        </tr></thead>
        <tbody>
          {summary && <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
            <td style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', whiteSpace: 'nowrap', color: 'var(--text2)' }}>总计</td>
            {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(summaryMetrics, metric)}</td>)}
          </tr>}
          {loading && visibleNodes.length === 0 ? <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            : !siteID ? <tr><td colSpan={99} style={{ padding: 42, textAlign: 'center', color: 'var(--text3)' }}>请在左侧切换到 Vooglam US 站点查看 Meta 投放数据</td></tr>
            : visibleNodes.length === 0 ? <tr><td colSpan={99} style={{ padding: 42, textAlign: 'center', color: 'var(--text3)' }}>当前日期或筛选条件下没有 Meta 投放数据，请调整筛选后重试</td></tr>
            : visibleNodes.map((node, index) => <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
              <td style={{ position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', width: '1%', minWidth: 160, maxWidth: 360 }}>
                <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                  {node.children.length > 0 && <button type="button" className="tree-toggle-btn" onClick={() => toggleNode(node.id)} aria-label={expanded.has(node.id) ? '收起' : '展开'} aria-expanded={expanded.has(node.id)}>{expanded.has(node.id) ? '−' : '+'}</button>}
                  {(node.children.length > 0 || node.depth > 0) && <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{dimensionLabel(node.dimKey, dimensions).replace(/维度$/, '')}</span>}
                  <TruncatedValueTooltip value={node.value} />
                </span>
              </td>
              {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{renderMetric(node.metrics, metric)}</td>)}
            </tr>)}
        </tbody>
      </table>
      {loading && visibleNodes.length > 0 && <TableLoadingOverlay />}
    </div>
  </section>;
}

export default function VooglamMetaDeliveryDashboard() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(6));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedTop, setAppliedTop] = useState<AppliedTopState>({ start: daysAgo(6), end: today(), dateGrain: 'day' });
  const [queryVersion, setQueryVersion] = useState(0);
  const initialQueryKeyRef = useRef('');
  const currentSite = useMemo(() => {
    if (brand !== 'vooglam') return null;
    const site = sites.find(item => item.id === siteId);
    return site && regionFromSiteCode(site.code) === 'us' ? site : null;
  }, [brand, siteId, sites]);
  const dateRange = useMemo(() => alignMarketingDateRange(rangeForPreset(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const commonProps = { userLevel: adContext?.data_level ?? 1, brandID: brandId, siteID: currentSite?.id ?? '', siteReady: sitesLoaded, baseParams: appliedTop, queryVersion };

  const query = () => {
    setAppliedTop({ start: dateRange.start, end: dateRange.end, dateGrain });
    setQueryVersion(version => version + 1);
  };
  useMarketingViewSection('page', {
    label: '公共日期', state: { dateGrain, datePreset, customStart, customEnd },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); },
    loading: false, query: () => { query(); },
    resolve: value => { const range = rangeForPreset(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
  });

  const reset = () => {
    const start = daysAgo(6);
    const end = today();
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(start);
    setCustomEnd(end);
    setAppliedTop({ start, end, dateGrain: 'day' });
    setQueryVersion(version => version + 1);
  };
  useEffect(() => {
    if (!brandId || !currentSite?.id) return;
    const key = `${brandId}:${currentSite.id}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedTop({ start: dateRange.start, end: dateRange.end, dateGrain });
    setQueryVersion(version => version + 1);
  }, [brandId, currentSite?.id, dateGrain, dateRange.end, dateRange.start]);

  if (!sitesLoaded) {
    return <div className="page-section visible"><div className="card" style={{ padding: 0 }}><TableLoadingState /></div></div>;
  }

  return <div className="page-section visible">
    <div className="dashboard-sticky-filters"><div className="data-filter-card"><div className="data-filter-row time">
      <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
      <DashboardQueryActions onReset={reset} onQuery={query} />
    </div></div></div>
    <MetaReportSection title="Meta投放广告" dataset="main" defaultDimensions={['date', 'user_segment_key']} dimensions={MAIN_DIMENSIONS} filters={MAIN_FILTERS} storageKey="vooglam_meta_main_hidden_cols" {...commonProps} />
    <MetaReportSection title="Meta投放广告_日期" dataset="main" defaultDimensions={['user_segment_key', 'date']} dimensionOrder="date_last" dimensions={MAIN_DIMENSIONS} filters={MAIN_FILTERS} storageKey="vooglam_meta_date_hidden_cols" {...commonProps} />
    <MetaReportSection title="Meta投放广告-地区" dataset="country" defaultDimensions={['date']} dimensionOrder="country_first_date_last" dimensions={COUNTRY_DIMENSIONS} filters={COUNTRY_FILTERS} storageKey="vooglam_meta_country_hidden_cols" {...commonProps} />
  </div>;
}
